<template>
  <div :id="chartId" style="height: 300px; width: 100%;" />
</template>

<script lang="ts">
import { PropType, defineComponent, markRaw } from 'vue';
import type * as echarts from 'echarts';

import { Nullable } from '@/utils/types';
import { IGroupedBarChartData } from '@/bundles/Visualizations/types';
import { loadEcharts } from '@/bundles/Visualizations/helpers/loadEcharts';
import { EchartsThemeMixin } from '@/bundles/Visualizations/mixins/EchartsThemeMixin';

interface ITooltipParam {
  seriesIndex: number;
  seriesName: string;
  value: number | null;
  marker: string;
  axisValueLabel: string;
}

interface IEChartBarLabelRotation {
  myChart: Nullable<echarts.ECharts>;
  isDestroyed: boolean;
}

export default defineComponent({
  name: 'EChartBarLabelRotation',

  mixins: [EchartsThemeMixin],

  props: {
    chartId: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    chartData: {
      type: Object as PropType<IGroupedBarChartData>,
      required: true
    },
  },

  data: (): IEChartBarLabelRotation => ({
    myChart: null,
    isDestroyed: false,
  }),

  watch: {
    echartsTheme () {
      this.myChart?.dispose();
      this.myChart = null;
      this.loadChart();
    },
    chartData: {
      handler () {
        this.loadChart();
      },
      deep: true,
    }
  },

  mounted () {
    this.loadChart();
  },

  beforeUnmount () {
    this.isDestroyed = true;
    this.myChart?.dispose();
    this.myChart = null;
  },

  methods: {
    formatTooltipValue (param: ITooltipParam): string {
      const seriesConfig = this.chartData.series[param.seriesIndex];
      const valueFormatter = seriesConfig?.tooltip?.valueFormatter;

      return valueFormatter ? valueFormatter(param.value as number) : String(param.value);
    },

    formatTooltip (params: ITooltipParam[]): string {
      const items = params
        .filter(p => p.value != null)
        .sort((a, b) => b.value! - a.value!);

      if (!items.length) return '';

      const header = items[0].axisValueLabel;
      const rows = items.map(p => `${p.marker}${p.seriesName} <b>${this.formatTooltipValue(p)}</b>`);

      return [header, ...rows].join('<br/>');
    },

    async loadChart () {
      if (this.isDestroyed) {
        return;
      }

      const container = document.getElementById(this.chartId);

      if (!container) {
        return;
      }

      const echarts = await loadEcharts();

      if (this.isDestroyed) {
        return;
      }

      if (!this.myChart) {
        // Kept out of Vue's reactivity: a proxied ECharts instance breaks its internal identity checks.
        this.myChart = markRaw(echarts.init(container, this.echartsTheme));
      }

      this.myChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: this.formatTooltip
        },
        legend: {
          data: this.chartData.series.map(s => s.name),
          type: 'scroll',
          bottom: 0,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          axisTick: { show: false },
          data: this.chartData.categories,
        },
        yAxis: {
          type: 'value',
        },
        series: this.chartData.series,
      },
      true
      );
    }
  }
})
</script>
